import React, { useRef, useState } from 'react';
import './KpDropzone.css';

/**
 * KpDropzone – uppladdningsyta (DS Z6). Spec: docs/components/kp-dropzone.md.
 * Dra och släpp eller välj fil med knapp/tangentbord. Kontrollerar typ och storlek och
 * skickar bara godkända filer till onFiles; själva uppladdningen sköter anroparen.
 * Filval stegvis: varje val läggs till (anroparen håller listan, t.ex. med KpChip).
 *
 * Props:
 *  - onFiles: (files: File[]) => void       godkända filer
 *  - accept?: string                        t.ex. ".pdf,.jpg,.jpeg,.png,.heic"
 *  - maxBytes?: number                      maxstorlek per fil
 *  - multiple?: boolean                     standard true
 *  - title?: string                         standard "Dra hit filer eller välj fil"
 *  - help?: string                          formatrad, t.ex. "PDF eller bild · högst 20 MB per fil"
 *  - disabled?: boolean
 *  - className?: string
 */
function tillatnaAndelser(accept) {
  return String(accept || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function godkandTyp(fil, regler) {
  if (!regler.length) return true;
  const namn = (fil.name || '').toLowerCase();
  const typ = (fil.type || '').toLowerCase();
  return regler.some((r) => {
    if (r.startsWith('.')) return namn.endsWith(r);
    if (r.endsWith('/*')) return typ.startsWith(r.slice(0, -1));
    return typ === r;
  });
}

export function formateraStorlek(bytes) {
  if (bytes >= 1024 * 1024) return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`.replace('.', ',');
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} byte`;
}

export default function KpDropzone({
  onFiles,
  accept,
  maxBytes,
  multiple = true,
  title = 'Dra hit filer eller välj fil',
  help,
  disabled = false,
  className = '',
}) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [fel, setFel] = useState([]);

  const ta = (lista) => {
    const regler = tillatnaAndelser(accept);
    const ok = [];
    const nyaFel = [];
    Array.from(lista || []).forEach((f) => {
      if (!godkandTyp(f, regler)) nyaFel.push(`${f.name}: filtypen tas inte emot.`);
      else if (typeof maxBytes === 'number' && f.size > maxBytes) nyaFel.push(`${f.name}: filen är större än ${formateraStorlek(maxBytes)}.`);
      else ok.push(f);
    });
    const valda = multiple ? ok : ok.slice(0, 1);
    setFel(nyaFel);
    if (valda.length && typeof onFiles === 'function') onFiles(valda);
  };

  const oppna = () => { if (!disabled && inputRef.current) inputRef.current.click(); };
  const cls = ['kp-dropzone', over ? 'kp-dropzone--over' : '', disabled ? 'kp-dropzone--disabled' : '', className].filter(Boolean).join(' ');

  return (
    <div>
      <div
        className={cls}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={oppna}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); oppna(); } }}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) ta(e.dataTransfer && e.dataTransfer.files); }}
      >
        <svg className="kp-dropzone__ikon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 15v4h16v-4" /><path d="M12 15V3" /><path d="M7 8l5-5 5 5" />
        </svg>
        <span>
          <span className="kp-dropzone__titel">{title}</span>
          {help ? <span className="kp-dropzone__hjalp">{help}</span> : null}
        </span>
        <input
          ref={inputRef}
          type="file"
          className="kp-dropzone__input"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => { ta(e.target.files); e.target.value = ''; }}
        />
      </div>
      {fel.length ? (
        <ul className="kp-dropzone-fel" role="alert">
          {fel.map((t) => <li key={t}>{t}</li>)}
        </ul>
      ) : null}
    </div>
  );
}
